import React, { useContext, useState, useEffect } from "react";

import {
  Stack,
  Grid2 as Grid,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
} from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { useMutation } from "@apollo/client";
import moment from 'moment'

//   icon
import { BiMinusCircle } from "react-icons/bi";

//Srcs
import "../../Style/dialogStyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { translateLauguage } from "../../Function/Translate";
import { SelectAcademicYear, SelectSchool } from "../../Function/DynamicSelect";
import ImportStudentFromCSV from "./ImportStudentFromCSV";
import { IMPORT_STUDENT_FROM_CSV_FILE } from "../../Schema/Student";

export default function ImportStudentDialog({ open, handleClose, setRefetch }) {
  // ===================== Change Language ==========================
  const { language, setAlert } = useContext(AuthContext);
  const { t } = translateLauguage(language);

  const [loading, setLoading] = useState(false);

  const [csvData, setCSVData] = useState([]);

  const [academicYearVal, setAcademicYearVal] = useState({ id: "", title: "" });
  const [schoolVal, setSchoolVal] = useState({ id: "", title: "" });

  //======================== Create User =========================
  const [importStudentFromCSVFile] = useMutation(IMPORT_STUDENT_FROM_CSV_FILE, {
    onCompleted: ({ importStudentFromCSVFile }) => {
      setLoading(false);
      if (importStudentFromCSVFile?.is_success === true) {
        setAlert(true, "success", importStudentFromCSVFile?.message);
        handleClose();
        resetForm();
        setCSVData([]);
        setRefetch();
      } else {
        setAlert(true, "error", importStudentFromCSVFile?.message);
 
        handleClose();
      }
    },
    onError: (error) => {
      setLoading(false);
      if(error?.message?.message_kh && error?.message?.message_en){
        setAlert(true, "error", error?.message);
      }else{
        setAlert(true, "error", {message_kh: error?.message, message_en: error?.message})
      }
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    full_name_kh: Yup.string(),
    full_name_latin: Yup.string(),
    gender: Yup.string(),
    group: Yup.string(),
    level: Yup.string(),
    student_id: Yup.string(),
    schoolId: Yup.string().required(t("require")),
    academicYearId: Yup.string().required(t("require")),
  });

  const formik = useFormik({
    initialValues: {
      full_name_kh: "",
      full_name_latin: "",
      gender: "",
      group: "",
      level: "",
      student_id: "",
      schoolId: "",
      academicYearId: "",
    },

    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      const submitData = csvData?.map((e) => {
        return {
          student_id: parseInt(e?.ID),
          full_name_kh: e["Khmer Name"],
          full_name_latin: e["Latin Name"],
          gender: e?.Gender,
          group: e?.Group,
          level: e?.Level,
          status: e?.Status,
          drop_out_date: e["Drop out date"] ? moment(e["Drop out date"]).format("YYYY-MM-DD") : "",
        };
      });


      importStudentFromCSVFile({
        variables: {
          schoolId: values?.schoolId,
          academicYearId: values?.academicYearId,
          input: submitData,
        },
      });
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    resetForm,
    setFieldValue,
  } = formik;

  useEffect(() => {
    setFieldValue("schoolId", schoolVal?.id ?? "");
  }, [schoolVal?.id]);

  useEffect(() => {
    setFieldValue("academicYearId", academicYearVal?.id ?? "");
  }, [academicYearVal?.id]);

  useEffect(() => {
    resetForm();
    setAcademicYearVal({ id: "", title: "" });
    setSchoolVal({ id: "", title: "" });
    setCSVData([]);
  }, [open]);

  return (
    <Dialog open={open} className="dialog-container" fullWidth maxWidth="sm">
      <DialogTitle sx={{ padding: "10px 15px 10px 20px " }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" justifyContent="center">
            <Typography
              className={language === "en" ? "dialog-title" : "dialog-title-kh"}
            >
              {t("import_csv_file")}
            </Typography>
          </Stack>
          <Stack direction="column" justifyContent="center">
            <IconButton onClick={handleClose}>
              <BiMinusCircle className="close-icon" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("academic_year")}
                </Typography>
                <SelectAcademicYear
                  selectedVal={academicYearVal}
                  setSelectedVal={setAcademicYearVal}
                />
                {!!errors.academicYearId && touched.academicYearId && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.academicYearId}
                  </FormHelperText>
                )}
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("school")}
                </Typography>
                <SelectSchool
                  selectedVal={schoolVal}
                  setSelectedVal={setSchoolVal}
                  disabled={academicYearVal?.id ? false : true}
                />
                {!!errors.schoolId && touched.schoolId && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.schoolId}
                  </FormHelperText>
                )}
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("import")}
                </Typography>
                <Stack direction="row" justifyContent="center">
                  <ImportStudentFromCSV setCSVData={setCSVData} open={open} />
                </Stack>
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
      <DialogActions>
        <Grid size={{ xs: 12 }} className="create-container">
          {loading ? (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
            >
              {t("loading")}
            </Button>
          ) : (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
              onClick={handleSubmit}
              // disabled={csvData.length >= 1 ? true : false}
            >
              {t("import")}
            </Button>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
